import type { Clock } from './clock'

/**
 * Delayed-callback scheduler driven by an injectable Clock instead of setTimeout.
 * setTimeout runs on real wall-clock time regardless of test mode, so anything using
 * it (e.g. a "respawn 1.2s after being hit" timer) would desync from a ManualClock
 * and break frame-for-frame reproducibility. Call update() once per GameLoop update()
 * tick to fire any callbacks whose delay has elapsed.
 */
export class Scheduler {
  private readonly clock: Clock
  private nextId = 1
  private pending = new Map<number, { dueMs: number; fn: () => void }>()

  constructor(clock: Clock) {
    this.clock = clock
  }

  /** Schedule fn to run after delayMs of simulated time. Returns an id usable with cancel(). */
  schedule(delayMs: number, fn: () => void): number {
    const id = this.nextId++
    this.pending.set(id, { dueMs: this.clock.now() + delayMs, fn })
    return id
  }

  cancel(id: number): void {
    this.pending.delete(id)
  }

  /** Runs (and removes) every pending callback whose due time has passed. */
  update(): void {
    const now = this.clock.now()
    for (const [id, entry] of this.pending) {
      if (entry.dueMs <= now) {
        this.pending.delete(id)
        entry.fn()
      }
    }
  }
}
